import { type ReactNode, useEffect } from 'react';
import { onIdTokenChanged, getIdTokenResult } from 'firebase/auth';
import { useTranslation } from 'react-i18next';
import { auth } from '@/lib/backend/firebase';
import { idleLockMinutes } from '@/lib/backend/config';
import { Spinner } from '@/lib/ui/spinner';
import { parseClaims, sessionStatusFor } from './claims';
import { useAuthStore } from './auth-store';
import { signOutEverywhere } from './sign-out';
import { rememberedUntil, clearRememberMe } from './remember-me';

/**
 * Subscribes to Firebase Auth and keeps the auth store in sync. Also enforces the idle auto-lock
 * for this privileged desk tool (docs/05 idle/expiry posture) - the backend imposes no idle
 * timeout, so the dashboard imposes its own. A session the user asked to be remembered
 * (remember-me.ts) skips the idle lock and instead ends when its 30-day window closes.
 */
export function AuthProvider({ children }: { children: ReactNode }) {
  const status = useAuthStore((s) => s.status);
  const { t } = useTranslation();

  // Auth subscription.
  useEffect(() => {
    const unsub = onIdTokenChanged(auth, async (user) => {
      if (!user) {
        const notice = useAuthStore.getState().signInNotice;
        useAuthStore.getState().setSignedOut(notice);
        return;
      }
      try {
        const result = await getIdTokenResult(user);
        const claims = parseClaims(result.claims);
        const session = { uid: user.uid, email: user.email, claims };
        // Admit BOTH admin staff and therapists to a signed-in session; the route guards
        // (RequireAuth vs RequireTherapist) then send each to its own workspace.
        if (sessionStatusFor(claims) === 'signedIn') useAuthStore.getState().setSignedIn(session);
        else useAuthStore.getState().setNoAccess(session);
      } catch {
        // A failed token fetch (e.g. revoked refresh token) is a terminal session event.
        void signOutEverywhere(t('auth.sessionExpired'));
      }
    });
    return unsub;
  }, [t]);

  // Session lock - only while signed in. Remembered: poll for the window's end (setTimeout cannot
  // span 30 days). Otherwise: the idle auto-lock.
  useEffect(() => {
    if (status !== 'signedIn') return;
    if (rememberedUntil() !== null) {
      const check = () => {
        if (rememberedUntil() !== null) return;
        clearRememberMe();
        void signOutEverywhere(t('auth.rememberExpired'));
      };
      const poll = setInterval(check, 60_000);
      document.addEventListener('visibilitychange', check);
      return () => {
        clearInterval(poll);
        document.removeEventListener('visibilitychange', check);
      };
    }
    let timer: ReturnType<typeof setTimeout>;
    const reset = () => {
      clearTimeout(timer);
      timer = setTimeout(
        () => void signOutEverywhere(t('auth.idleLocked')),
        idleLockMinutes * 60_000,
      );
    };
    const events: (keyof WindowEventMap)[] = ['pointerdown', 'keydown', 'scroll'];
    events.forEach((e) => window.addEventListener(e, reset, { passive: true }));
    reset();
    return () => {
      clearTimeout(timer);
      events.forEach((e) => window.removeEventListener(e, reset));
    };
  }, [status, t]);

  if (status === 'loading') {
    return (
      <div className="flex min-h-screen items-center justify-center bg-surface-page text-ink-secondary">
        <Spinner className="size-6 text-brand-primary" />
      </div>
    );
  }

  return <>{children}</>;
}
